import React, { useEffect, useMemo, useRef, useState } from "react";

type Props = {
  id: string;
  openSide: "left" | "right" | null;
  onOpenSideChange: (id: string, side: "left" | "right" | null) => void;
  testId?: string;
  leftActions?: React.ReactNode;
  rightActions?: React.ReactNode;
  children: React.ReactNode;
};

const OVER_REVEAL_PX = 8; // ~2mm visual overhang so action isn't flush with the edge.
const MIN_SWIPE_THRESHOLD_PX = 24;

export function SwipeRow({ id, openSide, onOpenSideChange, testId, leftActions, rightActions, children }: Props) {
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const start = useRef<{ x: number; y: number } | null>(null);
  const decided = useRef<"h" | "v" | null>(null);
  const leftActionsRef = useRef<HTMLDivElement | null>(null);
  const rightActionsRef = useRef<HTMLDivElement | null>(null);
  const [leftActionsWidth, setLeftActionsWidth] = useState(0);
  const [rightActionsWidth, setRightActionsWidth] = useState(0);

  const hasLeftActions = Boolean(leftActions);
  const hasRightActions = Boolean(rightActions);
  const leftRevealPx = useMemo(
    () => (hasLeftActions ? Math.max(0, Math.round(leftActionsWidth + OVER_REVEAL_PX)) : 0),
    [hasLeftActions, leftActionsWidth],
  );
  const rightRevealPx = useMemo(
    () => (hasRightActions ? Math.max(0, Math.round(rightActionsWidth + OVER_REVEAL_PX)) : 0),
    [hasRightActions, rightActionsWidth],
  );
  const openX = useMemo(() => {
    if (openSide === "left") return leftRevealPx;
    if (openSide === "right") return -rightRevealPx;
    return 0;
  }, [leftRevealPx, openSide, rightRevealPx]);

  useEffect(() => {
    // Keep local transform in sync with open state when not actively dragging.
    if (!dragging) setDx(openX);
  }, [openX, dragging]);

  useEffect(() => {
    const leftEl = leftActionsRef.current;
    const rightEl = rightActionsRef.current;
    if (!leftEl && !rightEl) return;

    const measure = () => {
      setLeftActionsWidth(leftEl ? leftEl.getBoundingClientRect().width : 0);
      setRightActionsWidth(rightEl ? rightEl.getBoundingClientRect().width : 0);
    };
    measure();

    const ro = new ResizeObserver(measure);
    if (leftEl) ro.observe(leftEl);
    if (rightEl) ro.observe(rightEl);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [leftActions, rightActions]);

  useEffect(() => {
    if (!openSide) return;
    const onScroll = () => onOpenSideChange(id, null);
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [id, onOpenSideChange, openSide]);

  function onPointerDown(e: React.PointerEvent) {
    const targetEl = e.target as HTMLElement | null;
    if (targetEl?.closest("button, a, input, textarea, select, label")) return;
    start.current = { x: e.clientX, y: e.clientY };
    decided.current = null;
    setDragging(false);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!start.current) return;
    const dxx = e.clientX - start.current.x;
    const dyy = e.clientY - start.current.y;

    if (!decided.current) {
      if (Math.abs(dxx) < 8 && Math.abs(dyy) < 8) return;
      decided.current = Math.abs(dxx) > Math.abs(dyy) ? "h" : "v";
    }
    if (decided.current !== "h") return;

    // Horizontal swipe: prevent default scrolling behavior.
    e.preventDefault();
    setDragging(true);

    // Left swipe => reveal right actions, right swipe => reveal left actions.
    const base = openSide === "left" ? leftRevealPx : openSide === "right" ? -rightRevealPx : 0;
    let next = base + dxx;
    next = Math.min(leftRevealPx, Math.max(-rightRevealPx, next));
    setDx(next);
  }

  function onPointerUp(e: React.PointerEvent) {
    if (!start.current) return;
    start.current = null;

    if (decided.current !== "h") {
      decided.current = null;
      setDragging(false);
      return;
    }

    const leftThreshold = Math.max(MIN_SWIPE_THRESHOLD_PX, leftRevealPx * 0.35);
    const rightThreshold = Math.max(MIN_SWIPE_THRESHOLD_PX, rightRevealPx * 0.35);
    if (dx > leftThreshold && leftRevealPx > 0) {
      onOpenSideChange(id, "left");
    } else if (dx < -rightThreshold && rightRevealPx > 0) {
      onOpenSideChange(id, "right");
    } else {
      onOpenSideChange(id, null);
    }
    decided.current = null;
    setDragging(false);
  }

  return (
    <div
      data-testid={testId}
      style={{
        position: "relative",
        overflow: "hidden",
        borderRadius: 12,
        border: "1px solid var(--swk-border)",
        background: "var(--swk-card)",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div
        style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "stretch",
        justifyContent: "space-between",
        zIndex: 0,
      }}
    >
      <div
        ref={leftActionsRef}
        style={{
          display: "flex",
          alignItems: "center",
          gap: hasLeftActions ? 8 : 0,
          padding: hasLeftActions ? 10 : 0,
          opacity: hasLeftActions && (openSide === "left" || (dragging && dx > 0)) ? 1 : 0,
        }}
      >
        {leftActions}
      </div>
      <div
        ref={rightActionsRef}
        style={{
          display: "flex",
          alignItems: "center",
          gap: hasRightActions ? 8 : 0,
          padding: hasRightActions ? 10 : 0,
          opacity: hasRightActions && (openSide === "right" || (dragging && dx < 0)) ? 1 : 0,
        }}
      >
        {rightActions}
      </div>
      </div>

      <div
        style={{
          transform: `translateX(${dx}px)`,
          transition: dragging ? "none" : "transform 180ms ease",
          touchAction: "pan-y",
          background: "var(--swk-card)",
          padding: "10px 12px",
          position: "relative",
          zIndex: 1,
          pointerEvents: openSide ? "none" : "auto",
        }}
        onClick={(e) => {
          if (openSide) {
            // When actions are visible, first tap closes to avoid accidental activation.
            e.preventDefault();
            e.stopPropagation();
            onOpenSideChange(id, null);
          }
        }}
      >
        {children}
      </div>
    </div>
  );
}
